import React, {useEffect, useMemo} from 'react';
import {Pages} from "../components/layout";
import BackendForm from "../components/forms/BackendForm";
import {useNavigate} from "react-router";
import {routes} from "../utils";
import Description from "../components/Description";
import {Link, useSearchParams} from "react-router-dom";
import SignUpFields, {signUpCodeName, signUpInitialValues} from "./partials/SignUpFields";
import {backendEndpoints} from "../libs/backendEndpoints";
import useAuth from "../components/hooks/useAuth";
import {concepts, Course, Group, ShareRequest} from "fearless-common";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import LoginPage from "./LoginPage";
import FormFiller from "../components/forms/FormFiller";
import {Alert, useTitle} from "fearless-common-ui";

const signupFormId = "sign-up-form";
const signUpAnswers = [
    {
        title: 'new @divvly',
        answers: [
            { name: 'username', value: `user_${Date.now()}` },
            { name: 'first_name', value: `FakeName` },
            { name: 'last_name', value: `FakeSurname` },
            { name: 'organisation', value: `FakeOrganisation` },
            { name: 'password', value: `password` },
            { name: 'password_copy', value: `password` },
            { name: 'email', value: `user_${Date.now()}@divvly.com` },
        ],
    },
]

function MessageWrapper({ message }: { message: string}) {
    return (
      <>
        <Alert variant="success">{message}</Alert>
      </>
    );
}

/**
 * Public sign-up page. Renders either:
 *   - the bare account-creation form (default landing flow), or
 *   - a side-by-side Login + sign-up layout when the user arrived via a group
 *     invitation link (`group` prop set), or
 *   - a header tailored to share-request access when `shareRequests` is set.
 *
 * Query-string driven behavior (all read from the current URL):
 *   - `userFirstName` — name of the inviting user, used in the invitation copy.
 *   - `referralId` / `referralCode` — passed through to the backend so the new
 *     account can be attributed to a referral link. When `referralCode` is set,
 *     the sign-up-code field is replaced with a success message from
 *     `referralMessage`.
 *   - `signUpCode` — pre-fills the sign-up code field.
 *   - `redirect` — when present, forwarded to the backend as `redirect_url` in
 *     the sign-up payload so the backend can route the user to that location
 *     after email verification. Also forwarded onto the "Sign in here" link so
 *     a visitor who realises they already have an account doesn't lose the
 *     original destination by bouncing through the login page.
 *
 * The `redirect` *prop* (distinct from the query param) provides a fallback
 * destination for the "Sign in here" link only — it does not reach the
 * backend. The query param wins over the prop when both are set.
 *
 * Authenticated users are redirected to {@link routes.landing} on mount.
 */
export default function SignUp({ group, course, shareRequests, redirect }: {
    /** When set, render the group-invitation variant (heading + Login form). */
    group?: Group;
    /** Reserved for future use; currently not rendered. */
    course?: Course;
    /** When non-empty, render the share-request variant of the heading. */
    shareRequests?: ShareRequest[];
    /** Pre-fills the "Sign in here" link's `?redirect=` target. Distinct from
     *  the `redirect` query param, which is forwarded to the backend. */
    redirect?: string;
}) {
    useTitle('Try it for free!')
    const navigate = useNavigate()
    const { user } = useAuth()
    const [sp] = useSearchParams();
    const userFirstName =  sp?.get('userFirstName');

    useEffect(() => {
        if (user) navigate(routes.landing);
    }, [user])

    const showForShareRequests = shareRequests?.length;
    const showForGroup = Boolean(group);

    const groupId = group?.id;
    const referralId = sp.get('referralId');
    const referralCode = sp.get('referralCode');
    const signUpCode = sp.get(signUpCodeName);
    const redirectParam = sp.get('redirect');
    const initialValues = useMemo(() => ({
        email: '',
        ...signUpInitialValues.Account,
        ...signUpInitialValues.UsernamePassword,
        groups: groupId ? [groupId] : [],
        custom_fields: {},
        referral_link_id: referralId,
        referral_link_code: referralCode,
        [signUpCodeName]: signUpCode,
        ...(redirectParam ? {redirect_url: redirectParam} : {}),
    }), [groupId, referralCode, referralId, signUpCode, redirectParam]);

    const form = (
      <BackendForm
        targetMethod="POST"
        enableReinitialize
        onSuccess={() => navigate(routes.signUpSuccess)}
        targetEndpoint={backendEndpoints.signUp}
        initialValues={initialValues}
        // Enable remember form to lower the friction to sign up
        rememberForm
        formId={signupFormId}
      >
          <h3>{showForGroup ? 'Sign up' : 'Create login'}</h3>
          <SignUpFields.UsernamePassword checkDuplicate/>
          <br/>
          <SignUpFields.Account showEmail/>
          <SignUpFields.InstituteFields/>
          <br/>
          {referralCode ? (
            <MessageWrapper message={sp.get('referralMessage') as string} />
          ) : <SignUpFields.SignUpCode />}

          <FormFiller answerSets={signUpAnswers} formId={signupFormId}/>
      </BackendForm>
    );

    return (
        <Pages.Centred width={showForGroup ? 'sm' : "xs"} padded className="position-relative">
            <h1 className="text-center">
                {showForGroup && `Join ${group?.name}`}
                {showForShareRequests && `Access the ${shareRequests.length} shared files `}
                {!showForGroup && !showForShareRequests && 'Try for free!'}
            </h1>
            {group && userFirstName && (
                <Description className="mb-0 text-center mx-auto">
                    You have been invited by <b>{userFirstName}</b> to join the {concepts.Group.toLowerCase()}.
                </Description>
            )}
            {!showForGroup && (
                <Description className="text-center">
                    Have an account already? Sign in&nbsp;
                    <Link
                        className="btn-link"
                        to={(redirectParam || redirect)
                            ? routes.loginWithRedirect((redirectParam || redirect) as string)
                            : routes.login}
                    >here</Link>.
                </Description>
            )}
            <div className="mt-5">
                {showForGroup
                    ? (
                        <Row>
                            <Col md={6} className="border-gray border-end pe-sm-5">
                                <h3>Login</h3>
                                <LoginPage.Form redirectUrl={window.location.href} />
                            </Col>
                            <Col md={6} className="ps-sm-5">
                                {form}
                            </Col>
                        </Row>
                    ) : form}
            </div>
        </Pages.Centred>
    );
}
